import { useFlags } from "launchdarkly-react-client-sdk";
import * as React from "react";
import { SyntheticEvent, useCallback, useEffect } from "react";
import { useWatch } from "react-hook-form";

import EditIcon from "@mui/icons-material/Edit";
import PrintIcon from "@mui/icons-material/Print";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import { format } from "date-fns";

import { error, neutral } from "app/themes/colors";
import CustomPrintFormPreviewDialog from "components/custom-print-form-preview/custom-print-form-preview-dialog.component";
import useCustomPrintFormPreview from "components/custom-print-form-preview/use-custom-print-form-preview.hook";
import FormTemplatesField from "components/form-templates-field";
import usePreviewPdfForm from "components/preview-pdf-form-dialog/use-preview-pdf-form.hook";
import {
  SIGN_AND_UPLOAD_APPOINTMENT_FORMS,
  WORD_PROCESSOR_FORMS
} from "constants/feature-flags";
import {
  APPOINTMENT_FORM_TEMPLATE_TYPE,
  CUSTOM_FORM_TEMPLATE_TYPE
} from "constants/form-templates";
import type { FormTemplate } from "services/types/form-templates.type";

import {
  type PatientFormsValues,
  useFormTemplates
} from "./use-form-templates.hook";

export default function AppointmentFormsDownload() {
  const flags = useFlags();
  const wordProcessorFormsEnabled = flags[WORD_PROCESSOR_FORMS];
  const isSignAndUploadEnabled = flags[SIGN_AND_UPLOAD_APPOINTMENT_FORMS];

  const {
    showDialog,
    setPatientId,
    handlePrint,
    formContent,
    isReady,
    setTemplate,
    setMetadata
  } = useCustomPrintFormPreview();

  const {
    control,
    onSubmit,
    isError,
    isLoading,
    appointment,
    patientId,
    onCreateForm,
    handleSubmitSignature,
    clearValues
  } = useFormTemplates();

  const values = useWatch({ control });
  const formTemplate = values.form_template as FormTemplate | null | undefined;

  useEffect(() => {
    if (
      wordProcessorFormsEnabled &&
      formTemplate?.type === CUSTOM_FORM_TEMPLATE_TYPE
    ) {
      const fileNamePostfix = !appointment
        ? ""
        : `${appointment?.id} - ${appointment?.appointmentType?.name} ${
            appointment?.startDate
              ? format(new Date(appointment?.startDate || ""), "yyyy-MM-dd")
              : ""
          } ${format(new Date(), "yyyyMMddHHmmss")}.pdf`;

      setPatientId(patientId);
      setMetadata({
        staffMember: appointment?.staffMember,
        signedAt: appointment.startDate,
        dateOfVisit: appointment.startDate,
        fileNamePostfix,
        clinicId: appointment?.clinic?.id
      });
      setTemplate(formTemplate);
    } else {
      setPatientId(undefined);
      setMetadata({});
      setTemplate(undefined);
    }
  }, [
    wordProcessorFormsEnabled,
    patientId,
    setPatientId,
    appointment,
    formTemplate,
    setTemplate,
    setMetadata
  ]);

  const isFormTemplate =
    formTemplate?.type === CUSTOM_FORM_TEMPLATE_TYPE &&
    wordProcessorFormsEnabled;

  useEffect(() => {
    if (isFormTemplate) {
      showDialog();
    }
  }, [isFormTemplate, showDialog]);

  const { loadPdfContent, handleShowPreviewModal } = usePreviewPdfForm();

  const isPrintDisabled =
    !formTemplate || isLoading || (isFormTemplate && !isReady);

  const generatePdfFnCallback = useCallback(
    async (data?: Record<string, any>) => {
      const { id, file } = await onCreateForm(values as PatientFormsValues);
      if (id) {
        const signatureFiles = data?.signature ? [data.signature] : [];
        return await handleSubmitSignature(signatureFiles, id);
      }
      return file;
    },
    [handleSubmitSignature, onCreateForm, values]
  );

  const handleShowPdfPreview = useCallback(async () => {
    const { id, file } = await onCreateForm(values as PatientFormsValues);
    const blob = id ? await handleSubmitSignature([], id) : file;
    loadPdfContent({
      blob: blob ?? file,
      generatePdfFn: generatePdfFnCallback
    });
    handleShowPreviewModal(`Preview ${formTemplate?.title}`);
  }, [
    formTemplate?.title,
    generatePdfFnCallback,
    handleSubmitSignature,
    handleShowPreviewModal,
    loadPdfContent,
    onCreateForm,
    values
  ]);

  const handleSubmit = useCallback(
    (e: SyntheticEvent) => {
      if (isFormTemplate) {
        e.preventDefault();
        handlePrint(formContent);
      } else {
        onSubmit(e);
      }
    },
    [formContent, handlePrint, isFormTemplate, onSubmit]
  );

  return (
    <>
      <Grid
        container
        component="form"
        onClick={(e: SyntheticEvent) => e.stopPropagation()}
        onSubmit={handleSubmit}
        direction="row"
        alignItems="center"
        spacing={1}
      >
        <Grid item xs={8} pb={1}>
          <FormTemplatesField
            control={control}
            name="form_template"
            label="Appointment Form"
            placeholder="Select form to Print"
            type={APPOINTMENT_FORM_TEMPLATE_TYPE}
            createYourOwnFormEnabled={wordProcessorFormsEnabled}
            noLabel
            beforeSelect={clearValues}
          />
        </Grid>
        <Grid item xs={4}>
          <Stack direction="row" columnGap={1} pl={1}>
            <IconButton type="submit" disabled={isPrintDisabled} size="small">
              {isLoading ? <CircularProgress size={24} /> : <PrintIcon />}
            </IconButton>

            {isSignAndUploadEnabled &&
              formTemplate?.type !== CUSTOM_FORM_TEMPLATE_TYPE && (
                <Box>
                  <Tooltip title="Sign Form and Upload to E-Docs">
                    {isPrintDisabled ? (
                      <span>
                        <IconButton
                          size="small"
                          onClick={handleShowPdfPreview}
                          disabled={isPrintDisabled}
                        >
                          {isLoading ? (
                            <CircularProgress size={24} />
                          ) : (
                            <EditIcon
                              style={{
                                height: "24px",
                                width: "24px",
                                fill: neutral[400]
                              }}
                            />
                          )}
                        </IconButton>
                      </span>
                    ) : (
                      <IconButton
                        size="small"
                        onClick={handleShowPdfPreview}
                        disabled={isPrintDisabled}
                      >
                        {isLoading ? (
                          <CircularProgress size={24} />
                        ) : (
                          <EditIcon
                            style={{
                              height: "24px",
                              width: "24px",
                              fill: neutral[800]
                            }}
                          />
                        )}
                      </IconButton>
                    )}
                  </Tooltip>
                </Box>
              )}

            {isFormTemplate && (
              <IconButton size="small" onClick={showDialog}>
                <EditIcon />
              </IconButton>
            )}
          </Stack>
        </Grid>
        {isError && (
          <Grid item xs={12}>
            <Typography variant="body1" fontWeight={700} color={error[700]}>
              An Error has Occurred, please reach out to support.
            </Typography>
          </Grid>
        )}
      </Grid>
      <CustomPrintFormPreviewDialog />
    </>
  );
}
